import React from 'react';
import { observer } from 'mobx-react-lite';
import editorStore from '../../stores/EditorStore';

export interface FrameSlot { x: number; y: number; w: number; h: number; }
export interface TemplateDefinition { id: string; name: string; frames: FrameSlot[]; }

const GAP = 0.03;
const M   = 0.04;

export const PHOTOBOOK_TEMPLATES: TemplateDefinition[] = [
  { id: 'single',     name: 'Full Page',      frames: [{ x: M, y: M, w: 1 - M * 2, h: 1 - M * 2 }] },
  { id: 'two-v',      name: '2 Stacked',      frames: [
    { x: M, y: M, w: 1 - M * 2, h: (1 - M * 2 - GAP) / 2 },
    { x: M, y: M + (1 - M * 2 - GAP) / 2 + GAP, w: 1 - M * 2, h: (1 - M * 2 - GAP) / 2 },
  ]},
  { id: 'two-h',      name: '2 Side by Side', frames: [
    { x: M, y: M, w: (1 - M * 2 - GAP) / 2, h: 1 - M * 2 },
    { x: M + (1 - M * 2 - GAP) / 2 + GAP, y: M, w: (1 - M * 2 - GAP) / 2, h: 1 - M * 2 },
  ]},
  { id: 'three-top',  name: '1 + 2 Grid',     frames: [
    { x: M, y: M, w: 1 - M * 2, h: 0.54 },
    { x: M, y: M + 0.54 + GAP, w: (1 - M * 2 - GAP) / 2, h: 1 - M - 0.54 - GAP - M },
    { x: M + (1 - M * 2 - GAP) / 2 + GAP, y: M + 0.54 + GAP, w: (1 - M * 2 - GAP) / 2, h: 1 - M - 0.54 - GAP - M },
  ]},
  { id: 'four',       name: '4 Photos',       frames: [
    { x: M, y: M, w: (1 - M * 2 - GAP) / 2, h: (1 - M * 2 - GAP) / 2 },
    { x: M + (1 - M * 2 - GAP) / 2 + GAP, y: M, w: (1 - M * 2 - GAP) / 2, h: (1 - M * 2 - GAP) / 2 },
    { x: M, y: M + (1 - M * 2 - GAP) / 2 + GAP, w: (1 - M * 2 - GAP) / 2, h: (1 - M * 2 - GAP) / 2 },
    { x: M + (1 - M * 2 - GAP) / 2 + GAP, y: M + (1 - M * 2 - GAP) / 2 + GAP, w: (1 - M * 2 - GAP) / 2, h: (1 - M * 2 - GAP) / 2 },
  ]},
  { id: 'text-bottom', name: 'Photo + Caption', frames: [{ x: M, y: M, w: 1 - M * 2, h: 0.7 }] },
];

function TemplateThumbnail({ template, size = 110 }: { template: TemplateDefinition; size?: number }) {
  const aspect = 1.35;
  const tw = size;
  const th = size * aspect;
  return (
    <svg viewBox={`0 0 ${tw} ${th}`} style={{ width: '100%', height: 'auto', display: 'block' }} xmlns="http://www.w3.org/2000/svg">
      <rect width={tw} height={th} fill="#1c1e2e" rx="4" />
      {template.frames.map((f, i) => {
        const x  = f.x * tw; const y  = f.y * th;
        const w  = f.w * tw; const h  = f.h * th;
        const cx = x + w / 2; const cy = y + h / 2;
        const ir = Math.min(w, h) * 0.16;
        return (
          <g key={i}>
            <rect x={x} y={y} width={w} height={h} fill="#252840" stroke="rgba(91,121,255,0.3)" strokeWidth="0.8" strokeDasharray="3 2" rx="2" />
            <circle cx={cx - ir * 0.9} cy={cy - ir * 0.5} r={ir * 0.45} fill="rgba(91,121,255,0.4)" />
            <polygon
              points={`${cx - ir * 1.4},${cy + ir * 0.9} ${cx},${cy - ir * 0.3} ${cx + ir * 1.4},${cy + ir * 0.9}`}
              fill="rgba(91,121,255,0.4)"
            />
          </g>
        );
      })}
      {template.id === 'text-bottom' && (
        <g>
          <rect x={M * tw} y={0.77 * th} width={(1 - M * 2) * tw} height={0.03 * th} fill="rgba(255,255,255,0.12)" rx="1" />
          <rect x={M * tw + 8} y={0.82 * th} width={(1 - M * 2) * tw * 0.6} height={0.025 * th} fill="rgba(255,255,255,0.07)" rx="1" />
        </g>
      )}
    </svg>
  );
}

interface PhotobookTemplatesProps { onTemplateApplied?: () => void; }

const PhotobookTemplates: React.FC<PhotobookTemplatesProps> = observer(({ onTemplateApplied }) => {
  const handleClick = (tpl: TemplateDefinition) => {
    editorStore.addPhotoFrames(tpl.frames);
    onTemplateApplied?.();
  };

  return (
    <div style={{ padding: 10, overflowY: 'auto', overflowX: 'hidden', height: '100%' }}>
      <p style={{ fontSize: 10, color: '#5c6290', marginBottom: 10, textAlign: 'center', letterSpacing: 0.3 }}>
        Click or drag a layout onto the page
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
        {PHOTOBOOK_TEMPLATES.map(tpl => (
          <div
            key={tpl.id}
            draggable
            onDragStart={e => {
              e.dataTransfer.setData('application/x-photobook-template', JSON.stringify(tpl));
              e.dataTransfer.effectAllowed = 'copy';
            }}
            onClick={() => handleClick(tpl)}
            style={{
              cursor: 'pointer', borderRadius: 8,
              border: '1.5px solid rgba(255,255,255,0.07)',
              background: '#1c1e2e',
              display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 6,
              padding: 6, userSelect: 'none',
              transition: 'all 150ms',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'rgba(91,121,255,0.5)';
              e.currentTarget.style.boxShadow   = '0 4px 16px rgba(91,121,255,0.2)';
              e.currentTarget.style.transform   = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)';
              e.currentTarget.style.boxShadow   = 'none';
              e.currentTarget.style.transform   = 'none';
            }}
          >
            <TemplateThumbnail template={tpl} size={100} />
            <span style={{ fontSize: 10, color: '#9da3c8', fontWeight: 500, textAlign: 'center' }}>{tpl.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

export default PhotobookTemplates;
